import { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  ClipboardList, 
  Gavel, 
  CheckSquare, 
  FileText, 
  Shield,
  Save,
  Clock,
  CheckCircle2,
  ChevronRight,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/useMobile";
import { AttendeesSection } from "./sections/AttendeesSection";
import { AgendaSection } from "./sections/AgendaSection";
import { MotionsSection } from "./sections/MotionsSection";
import { ActionItemsSection } from "./sections/ActionItemsSection";
import { NotesSection } from "./sections/NotesSection";
import { ResolutionsSection } from "./sections/ResolutionsSection";
import { ObservancesSection } from "./sections/ObservancesSection";
import { ConcludeMeetingModal } from "./ConcludeMeetingModal";

interface MeetingRecordingInterfaceProps {
  meetingId: string;
  unaId: string;
  taskId: string;
  initialData?: any;
  onSave?: (data: any) => void;
  // Intentionally unused here: phase completion is handled by WorkflowPhaseManager buttons.
  onPhaseComplete?: () => void;
}

type SectionType = "attendees" | "agenda" | "motions" | "actions" | "notes" | "resolutions" | "observances";

const SECTIONS = [
  { id: "attendees" as const, label: "Attendees", icon: Users },
  { id: "agenda" as const, label: "Agenda", icon: ClipboardList },
  { id: "motions" as const, label: "Motions", icon: Gavel },
  { id: "actions" as const, label: "Action Items", icon: CheckSquare },
  { id: "notes" as const, label: "Notes", icon: FileText },
  { id: "resolutions" as const, label: "Resolutions", icon: Shield },
  { id: "observances" as const, label: "Observances", icon: Sparkles },
];

export function MeetingRecordingInterface({
  meetingId,
  unaId,
  taskId,
  initialData,
  onSave,
  onPhaseComplete
}: MeetingRecordingInterfaceProps) {
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const [activeSection, setActiveSection] = useState<SectionType>("attendees");
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "unsaved">("saved");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [concludeModalOpen, setConcludeModalOpen] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Meeting data state
  const [meetingData, setMeetingData] = useState({
    expected_attendees: initialData?.expected_attendees || [],
    actual_attendees: initialData?.actual_attendees || [],
    special_guests: initialData?.special_guests || [],
    absent_members: initialData?.absent_members || [],
    quorum_required: initialData?.quorum_required || false,
    quorum_threshold: initialData?.quorum_threshold || null,
    quorum_met: initialData?.quorum_met || null,
    agenda_items: initialData?.agenda_items || [],
    motions: initialData?.motions || [],
    decisions: initialData?.decisions || [],
    action_items: initialData?.action_items || [],
    resolutions_noted: initialData?.resolutions_noted || [],
    general_notes: initialData?.general_notes || "",
    has_transcript: initialData?.has_transcript || false,
    transcript_text: initialData?.transcript_text || "",
    transcript_file_url: initialData?.transcript_file_url || "",
    is_first_meeting: initialData?.is_first_meeting || false,
    meeting_type: initialData?.meeting_type || "Officer Meeting",
    observances: initialData?.observances || {},
  });
  const [primaryOfficerRole, setPrimaryOfficerRole] = useState<string>("Secretary Treasurer");

  // Load primary officer role label from unas for Observances defaults.
  useEffect(() => {
    if (!unaId) return;
    (async () => {
      const { data } = await supabase.from("unas").select("primary_officer_role").eq("id", unaId).maybeSingle();
      if (data?.primary_officer_role) setPrimaryOfficerRole(data.primary_officer_role);
    })();
  }, [unaId]);
  
  // Detect if this is a first organizational meeting
  const isFirstMeeting = meetingData.is_first_meeting;

  // CRITICAL FIX: Load existing data from database on mount
  useEffect(() => {
    const loadMeetingData = async () => {
      if (!meetingId) {
        console.warn('No meetingId provided, using initialData');
        setIsLoadingData(false);
        return;
      }

      try {
        console.log('📖 Loading meeting data from database:', meetingId);
        
        const { data, error } = await supabase
          .from('meeting_minutes')
          .select('*')
          .eq('id', meetingId)
          .single();
        
        if (error) throw error;
        
        if (data) {
          console.log('✅ Loaded existing data:', data);
          
          // Priority: Database data OVERRIDES initialData
          // Note: is_first_meeting may not exist in DB schema, so we check for it safely
          const dbData = data as any;
          setMeetingData({
            expected_attendees: dbData.expected_attendees || initialData?.expected_attendees || [],
            actual_attendees: dbData.actual_attendees || [],
            special_guests: dbData.special_guests || [],
            absent_members: dbData.absent_members || [],
            quorum_required: dbData.quorum_required ?? initialData?.quorum_required ?? false,
            quorum_threshold: dbData.quorum_threshold ?? initialData?.quorum_threshold ?? null,
            quorum_met: dbData.quorum_met ?? null,
            agenda_items: dbData.agenda_items || [],
            motions: dbData.motions || [],
            decisions: dbData.decisions || [],
            action_items: dbData.action_items || [],
            resolutions_noted: dbData.resolutions_noted || [],
            general_notes: dbData.general_notes || "",
            has_transcript: dbData.has_transcript ?? initialData?.has_transcript ?? false,
            transcript_text: dbData.transcript_text || initialData?.transcript_text || "",
            transcript_file_url: dbData.transcript_file_url || initialData?.transcript_file || "",
            is_first_meeting: dbData.is_first_meeting ?? false,
            meeting_type: dbData.meeting_type || initialData?.meeting_type || "Officer Meeting",
            observances: dbData.observances || initialData?.observances || {},
          });
          
          setSaveStatus("saved");
        }
      } catch (error) {
        console.error('❌ Error loading meeting data:', error);
        toast({
          title: "Error loading data",
          description: "Could not load existing meeting data",
          variant: "destructive",
        });
      } finally {
        setIsLoadingData(false);
      }
    };
    
    loadMeetingData();
  }, [meetingId, toast]); // Run on mount and when meetingId changes

  // Calculate quorum
  const calculateQuorum = useCallback(() => {
    if (!meetingData.quorum_required || !meetingData.quorum_threshold) {
      return null;
    }
    const attendeeCount = meetingData.actual_attendees.length;
    const quorumMet = attendeeCount >= meetingData.quorum_threshold;
    return quorumMet;
  }, [meetingData.quorum_required, meetingData.quorum_threshold, meetingData.actual_attendees]);

  // Update quorum status when attendees change
  useEffect(() => {
    const quorumMet = calculateQuorum();
    if (quorumMet !== null && quorumMet !== meetingData.quorum_met) {
      setMeetingData(prev => ({ ...prev, quorum_met: quorumMet }));
      setSaveStatus("unsaved");
    }
  }, [calculateQuorum, meetingData.quorum_met]);

  // CRITICAL FIX #6: Enhanced auto-save function to sync action items
  const saveData = useCallback(async () => {
    if (saveStatus === "saved") return;

    setSaveStatus("saving");
    
    try {
      const { error } = await supabase
        .from("meeting_minutes")
        .update({
          ...meetingData,
          last_saved_at: new Date().toISOString(),
        })
        .eq("id", meetingId);

      if (error) throw error;

      // CRITICAL FIX #6: Sync action items to meeting_action_items table
      if (meetingData.action_items && meetingData.action_items.length > 0) {
        // Delete existing records first
        await supabase
          .from('meeting_action_items')
          .delete()
          .eq('meeting_minutes_id', meetingId);
        
        // Insert current action items
        const actionItemRecords = meetingData.action_items.map((item: any) => ({
          meeting_minutes_id: meetingId,
          una_id: unaId,
          description: item.description || item.item || '',
          assigned_to: item.assigned_to,
          due_date: item.due_date,
          priority: item.priority || 'medium',
          notes: item.notes || '',
          related_to: item.related_to || 'general',
          status: 'pending'
        }));
        
        const { error: actionError } = await supabase
          .from('meeting_action_items')
          .insert(actionItemRecords);
        
        if (actionError) {
          console.error('Failed to sync action items:', actionError);
        }
      }

      setSaveStatus("saved");
      setLastSaved(new Date());
      onSave?.(meetingData);
    } catch (error) {
      console.error("Error saving meeting data:", error);
      setSaveStatus("unsaved");
      toast({
        title: "Save failed",
        description: "Could not save meeting data. Please try again.",
        variant: "destructive",
      });
    }
  }, [meetingData, meetingId, unaId, saveStatus, onSave, toast]);

  // Manual save handler - ALWAYS saves regardless of saveStatus
  const handleManualSave = async () => {
    setSaveStatus("saving");
    
    try {
      const { error } = await supabase
        .from("meeting_minutes")
        .update({
          ...meetingData,
          last_saved_at: new Date().toISOString(),
        })
        .eq("id", meetingId);

      if (error) throw error;

      // Sync action items to meeting_action_items table
      if (meetingData.action_items && meetingData.action_items.length > 0) {
        await supabase
          .from('meeting_action_items')
          .delete()
          .eq('meeting_minutes_id', meetingId);
        
        const actionItemRecords = meetingData.action_items.map((item: any) => ({
          meeting_minutes_id: meetingId,
          una_id: unaId,
          description: item.description || item.item || '',
          assigned_to: item.assigned_to,
          due_date: item.due_date,
          priority: item.priority || 'medium',
          notes: item.notes || '',
          related_to: item.related_to || 'general',
          status: 'pending'
        }));
        
        const { error: actionError } = await supabase
          .from('meeting_action_items')
          .insert(actionItemRecords);
        
        if (actionError) {
          console.error('Failed to sync action items:', actionError);
        }
      }

      setSaveStatus("saved");
      setLastSaved(new Date());
      onSave?.(meetingData);
      
      toast({
        title: "Meeting Saved",
        description: "All meeting data has been saved successfully.",
      });
    } catch (error) {
      console.error("Error saving meeting data:", error);
      setSaveStatus("unsaved");
      toast({
        title: "Save failed",
        description: "Could not save meeting data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (saveStatus === "unsaved") {
        saveData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [saveData, saveStatus]);

  // Update meeting data and mark as unsaved
  const updateMeetingData = (updates: Partial<typeof meetingData>) => {
    setMeetingData(prev => ({ ...prev, ...updates }));
    setSaveStatus("unsaved");
  };

  // Section navigation helpers
  const getNextSection = useCallback((current: SectionType): SectionType | null => {
    const currentIndex = SECTIONS.findIndex(s => s.id === current);
    if (currentIndex < SECTIONS.length - 1) {
      return SECTIONS[currentIndex + 1].id;
    }
    return null;
  }, []);

  const getNextSectionLabel = useCallback((current: SectionType): string | null => {
    const next = getNextSection(current);
    if (!next) return null;
    return SECTIONS.find(s => s.id === next)?.label || null;
  }, [getNextSection]);

  const handleContinueToNext = useCallback(() => {
    const next = getNextSection(activeSection);
    if (next) {
      setActiveSection(next);
    }
  }, [activeSection, getNextSection]);

  // Section completion status checker
  const getSectionCompletionStatus = useCallback((sectionId: SectionType): boolean => {
    switch (sectionId) {
      case 'attendees':
        return meetingData.actual_attendees.length > 0 || isFirstMeeting;
      case 'agenda':
        return meetingData.agenda_items.length > 0;
      case 'motions':
        return meetingData.motions.length > 0;
      case 'actions':
        return meetingData.action_items.length > 0;
      case 'notes':
        return !!meetingData.general_notes?.trim();
      case 'resolutions':
        return meetingData.resolutions_noted.length > 0;
      case 'observances': {
        const o: any = meetingData.observances || {};
        return !!(o.invocation_given || o.doctrine_reaffirmed || o.benediction_given || o.financial_report_delivered || o.stewardship_assessment_recorded || o.prior_minutes_approval?.prior_date);
      }
      default:
        return false;
    }
  }, [meetingData, isFirstMeeting]);

  // Calculate completed sections count
  const completedSectionsCount = useMemo(() => {
    return SECTIONS.filter(s => getSectionCompletionStatus(s.id)).length;
  }, [getSectionCompletionStatus]);

  // CRITICAL FIX: Conclude meeting should unlock the next phase immediately (no page refresh)
  // Phase completion itself is handled by WorkflowPhaseManager via onPhaseComplete.
  const handleConcludeMeeting = async (endTime: string) => {
    try {
      // 1. Save final data
      await saveData();

      // 2. Update meeting status and end time
      const { error: updateError } = await supabase
        .from("meeting_minutes")
        .update({
          meeting_end_time: endTime,
          status: "reviewing",
        })
        .eq("id", meetingId);

      if (updateError) throw updateError;

      toast({
        title: "Meeting Concluded",
        description: "Saved successfully. The next phase is now unlocked.",
      });

      // 3. Tell the parent phase manager to complete this phase and advance.
      // This updates UI state instantly without a refresh.
      onPhaseComplete?.();
    } catch (error) {
      console.error("Error concluding meeting:", error);
      toast({
        title: "Error",
        description: "Failed to conclude meeting. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-12rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading meeting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-12rem)] gap-4">
      {/* Mobile Header with Section Selector */}
      {isMobile && (
        <Card className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Recording Meeting</h2>
              {/* Save Status */}
              <div className="flex items-center gap-1 text-xs">
                {saveStatus === "saved" && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                {saveStatus === "saving" && <Save className="h-3 w-3 animate-pulse text-blue-600" />}
                {saveStatus === "unsaved" && <Clock className="h-3 w-3 text-yellow-600" />}
              </div>
            </div>
            {meetingData.quorum_required && (
              <Badge variant={meetingData.quorum_met ? "default" : "destructive"} className="text-xs">
                {meetingData.actual_attendees.length}/{meetingData.quorum_threshold}
              </Badge>
            )}
          </div>
          
          {/* Horizontal Tab Navigation for Mobile */}
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <Button
                  key={section.id}
                  variant={isActive ? "secondary" : "ghost"}
                  size="sm"
                  className={cn(
                    "flex-shrink-0 gap-1 min-h-[40px] px-3",
                    isActive && "bg-secondary"
                  )}
                  onClick={() => setActiveSection(section.id)}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-xs">{section.label}</span>
                </Button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Desktop Sidebar Navigation */}
      {!isMobile && (
        <Card className="w-64 p-4 flex flex-col shrink-0">
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-2">Recording Meeting</h2>
            
            {/* Save Status */}
            <div className="flex items-center gap-2 text-sm">
              {saveStatus === "saved" && (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">
                    {lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : "All changes saved"}
                  </span>
                </>
              )}
              {saveStatus === "saving" && (
                <>
                  <Save className="h-4 w-4 animate-pulse text-blue-600" />
                  <span className="text-muted-foreground">Saving...</span>
                </>
              )}
              {saveStatus === "unsaved" && (
                <>
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <span className="text-muted-foreground">Unsaved changes</span>
                </>
              )}
            </div>
            
            {/* Quorum Status */}
            {meetingData.quorum_required && (
              <div className="mt-2">
                <Badge variant={meetingData.quorum_met ? "default" : "destructive"}>
                  Quorum: {meetingData.actual_attendees.length}/{meetingData.quorum_threshold}
                  {meetingData.quorum_met ? " ✓" : " ✗"}
                </Badge>
              </div>
            )}
          </div>

          <Separator className="mb-4" />

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{completedSectionsCount}/{SECTIONS.length}</span>
            </div>
            <Progress value={(completedSectionsCount / SECTIONS.length) * 100} className="h-2" />
          </div>

          {/* Navigation Sections */}
          <ScrollArea className="flex-1">
            <nav className="space-y-1">
              {SECTIONS.map((section, index) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                const isCompleted = getSectionCompletionStatus(section.id);
                const activeIndex = SECTIONS.findIndex(s => s.id === activeSection);
                const isNext = activeIndex + 1 === index;
                
                return (
                  <Button
                    key={section.id}
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-2 relative",
                      isActive && "bg-secondary",
                      isNext && !isActive && "border-l-2 border-primary"
                    )}
                    onClick={() => setActiveSection(section.id)}
                  >
                    {isCompleted && !isActive ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                    <span className="flex-1 text-left">{section.label}</span>
                    {isNext && !isActive && (
                      <Badge variant="outline" className="text-xs ml-auto">Next</Badge>
                    )}
                  </Button>
                );
              })}
            </nav>
          </ScrollArea>

          <Separator className="my-4" />

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleManualSave}
              disabled={saveStatus === "saving"}
            >
              <Save className="h-4 w-4 mr-2" />
              {saveStatus === "saving" ? "Saving..." : "Save Now"}
            </Button>
            
            <Button
              className="w-full"
              onClick={() => setConcludeModalOpen(true)}
            >
              Conclude Meeting
            </Button>
          </div>
        </Card>
      )}

      {/* Main Content Area */}
      <Card className="flex-1 p-4 md:p-6 min-h-0 flex flex-col">
        <ScrollArea className="flex-1">
          {activeSection === "attendees" && (
            <AttendeesSection
              data={meetingData}
              onChange={updateMeetingData}
              unaId={unaId}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "agenda" && (
            <AgendaSection
              data={meetingData}
              onChange={updateMeetingData}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "motions" && (
            <MotionsSection
              data={meetingData}
              onChange={updateMeetingData}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "actions" && (
            <ActionItemsSection
              data={meetingData}
              onChange={updateMeetingData}
              unaId={unaId}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "notes" && (
            <NotesSection
              data={meetingData}
              onChange={updateMeetingData}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "resolutions" && (
            <ResolutionsSection
              data={{
                ...meetingData,
                actual_attendees: meetingData.actual_attendees
              }}
              onChange={updateMeetingData}
              unaId={unaId}
              meetingId={meetingId}
              isFirstMeeting={isFirstMeeting}
            />
          )}
          {activeSection === "observances" && (
            <ObservancesSection
              data={meetingData}
              onChange={updateMeetingData}
              primaryOfficerRole={primaryOfficerRole}
            />
          )}
          
          {/* Continue to Next Section Button */}
          {getNextSection(activeSection) && (
            <div className="mt-8 pt-6 border-t">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  When you're done with <span className="font-medium">{SECTIONS.find(s => s.id === activeSection)?.label}</span>, continue to the next section.
                </p>
                <Button onClick={handleContinueToNext} className="shrink-0">
                  Continue to {getNextSectionLabel(activeSection)}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Last section - ready to conclude */}
          {!getNextSection(activeSection) && (
            <div className="mt-8 pt-6 border-t">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  You've reached the last section. When ready, conclude the meeting to proceed.
                </p>
                <Button onClick={() => setConcludeModalOpen(true)} className="shrink-0">
                  Conclude Meeting
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Mobile Sticky Action Buttons */}
      {isMobile && (
        <div className="sticky bottom-0 bg-background border-t p-3 flex gap-2">
          <Button
            variant="outline"
            className="flex-1 min-h-[44px]"
            onClick={handleManualSave}
            disabled={saveStatus === "saving"}
          >
            <Save className="h-4 w-4 mr-2" />
            {saveStatus === "saving" ? "Saving..." : "Save"}
          </Button>
          <Button
            className="flex-1 min-h-[44px]"
            onClick={() => setConcludeModalOpen(true)}
          >
            Conclude
          </Button>
        </div>
      )}

      {/* Conclude Meeting Modal */}
      <ConcludeMeetingModal
        open={concludeModalOpen}
        onOpenChange={setConcludeModalOpen}
        onConclude={handleConcludeMeeting}
        startTime={initialData?.meeting_start_time}
      />
    </div>
  );
}
